import { tryRecoverStaleAsset } from "@mixitone/util";
import { Fragment, Suspense, ComponentType, type JSX } from "react";
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from "react-router-dom";
import type { ActionFunction, RouteObject, LoaderFunction } from "react-router-dom";
import { generateModalRoutes, generatePreservedRoutes, generateRegularRoutes } from "../core";
import { ModalContextProvider } from "./context";
import { RouteLoadError } from "./RouteLoadError";

type Element = () => JSX.Element;
type Module = {
  default: Element;
  Loader?: LoaderFunction;
  Action?: ActionFunction;
  Catch?: Element;
  Pending?: Element;
};
export type ModalState = { path: string; params: Record<string, any>; id: string };

const PRESERVED = import.meta.glob<Module>("/src/pages/(_app|404).{jsx,tsx}", { eager: true });
const MODALS = import.meta.glob<Pick<Module, "default">>("/src/pages/**/[+]*.{jsx,tsx}", { eager: true });
const ROUTES = import.meta.glob<Module>([
  "/src/pages/**/[\\w[-]*.{jsx,tsx,mdx}",
  "!/src/pages/**/(_!(layout)*(/*)?|_app|404)*",
]);

const preservedRoutes = generatePreservedRoutes<Omit<Module, "Action">>(PRESERVED);
const modalRoutes = generateModalRoutes<Element>(MODALS);

const regularRoutes = generateRegularRoutes<RouteObject, () => Promise<Partial<Module>>>(
  ROUTES,
  (module, key) => {
    const index = /index\.(jsx|tsx|mdx)$/.test(key) && !key.includes("pages/index") ? { index: true } : {};

    return {
      ...index,
      errorElement: <RouteLoadError />,
      lazy: async () => {
        let loaded: Partial<Module>;
        try {
          loaded = await module();
        } catch (error) {
          if (tryRecoverStaleAsset(error)) {
            await new Promise<void>(() => {});
          }
          throw error;
        }

        const Default = loaded?.default || Fragment;
        const Pending = loaded?.Pending;
        const Page = () =>
          Pending ? <Suspense fallback={<Pending />} children={<Default />} /> : <Default />;

        return {
          Component: Page,
          ErrorBoundary: loaded?.Catch,
          loader: loaded?.Loader,
          action: loaded?.Action,
        };
      },
    };
  },
);

const _app = preservedRoutes?.["_app"];
const _404 = preservedRoutes?.["404"];

const Default = _app?.default || Outlet;

const ModalWrapper = ({ Modal, params }: { Modal: Element; params: Record<string, any> }) => (
  <ModalContextProvider value={{ params }}>
    <Modal />
  </ModalContextProvider>
);

const Modals = () => {
  const location = useLocation();
  const modals: ModalState[] = location.state?.modals ?? [];
  return (
    <div className="modals">
      {modals.map((modal) => {
        const Modal = modalRoutes[modal.path];
        return Modal ? <ModalWrapper key={modal.id} Modal={Modal} params={modal.params} /> : null;
      })}
    </div>
  );
};

export { Modals };

interface LayoutProps {
  includeModals?: boolean;
}

const Layout = ({ includeModals = true }: LayoutProps) => (
  <>
    <Default />
    {includeModals && <Modals />}
  </>
);

const App = ({ includeModals }: LayoutProps) =>
  _app?.Pending ? (
    <Suspense fallback={<_app.Pending />}>
      <Layout includeModals={includeModals} />
    </Suspense>
  ) : (
    <Layout includeModals={includeModals} />
  );

const app = {
  Component: _app?.default ? App : Layout,
  ErrorBoundary: _app?.Catch,
  errorElement: <RouteLoadError />,
  loader: _app?.Loader,
};
const fallback = { path: "*", Component: _404?.default || Fragment };

export const routes: RouteObject[] = [{ ...app, children: [...regularRoutes, fallback] }];

interface RoutesProps {
  includeModals?: boolean;
}

let router: ReturnType<typeof createBrowserRouter>;
const createRouter = (includeModals: boolean) => {
  if (!router) {
    const appRoute = routes[0];
    const modifiedApp = {
      ...appRoute,
      Component: () => {
        const Comp = appRoute.Component! as ComponentType<LayoutProps>;
        return <Comp includeModals={includeModals} />;
      },
    };
    router = createBrowserRouter([modifiedApp]);
  }
  return router;
};

export const Routes = ({ includeModals = true }: RoutesProps) => (
  <RouterProvider router={createRouter(includeModals)} />
);
